import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { QRCodeCanvas, QRCodeSVG } from "qrcode.react";
import { Loader2, Save, Upload, QrCode, Download, ExternalLink, IdCard } from "lucide-react";
import { API } from "../../lib/adventureApi";
import { authHeader } from "../../lib/auth";

const FIELDS = [
  ["title", "Headline", "Responsible Adventures"],
  ["tagline", "Tagline", "Beyond the ordinary — journeys with heart…"],
  ["raj_name", "Name", "Raj Tamang"],
  ["raj_role", "Role", "Founder, Responsible Adventures"],
  ["raj_phone", "Phone (Call)", "+977 9801082108"],
  ["raj_whatsapp", "WhatsApp number", "9779801082108"],
  ["raj_email", "Email", "raj@responsibleadventures.com"],
];

export default function CallingCardAdmin() {
  const [form, setForm] = useState({});
  const [hasVideo, setHasVideo] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [hasPoster, setHasPoster] = useState(false);
  const [posterBust, setPosterBust] = useState(Date.now());
  const [bust, setBust] = useState(Date.now());
  const svgWrapRef = useRef(null);

  const introUrl = `${window.location.origin}/intro`;

  const load = () => fetch(`${API}/calling-card`).then((r) => r.json()).then((d) => {
    setForm(d); setHasVideo(!!d.has_video); setHasPoster(!!d.has_poster);
  });
  useEffect(() => { load().catch(() => toast.error("Could not load")).finally(() => setLoading(false)); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/admin/calling-card`, {
        method: "PUT", headers: { "Content-Type": "application/json", ...authHeader() },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "");
      toast.success("Calling card saved");
    } catch (e) { toast.error(e.message || "Save failed"); } finally { setSaving(false); }
  };

  const onUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("video/")) { toast.error("Please choose a video file"); return; }
    setUploading(true); setProgress(0);
    const fd = new FormData(); fd.append("file", file);
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${API}/admin/calling-card/video`);
    const h = authHeader(); if (h.Authorization) xhr.setRequestHeader("Authorization", h.Authorization);
    xhr.upload.onprogress = (ev) => { if (ev.lengthComputable) setProgress(Math.round((ev.loaded / ev.total) * 100)); };
    xhr.onload = () => {
      setUploading(false);
      if (xhr.status >= 200 && xhr.status < 300) {
        setHasVideo(true); setBust(Date.now());
        toast.success("Trailer uploaded", { description: "Live on the /intro page." });
      } else {
        let msg = "Upload failed"; try { msg = JSON.parse(xhr.responseText).detail || msg; } catch (_) {}
        toast.error(msg);
      }
    };
    xhr.onerror = () => { setUploading(false); toast.error("Upload failed"); };
    xhr.send(fd);
  };

  const onPoster = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image"); return; }
    const fd = new FormData(); fd.append("file", file);
    try {
      const res = await fetch(`${API}/admin/calling-card/poster`, { method: "POST", headers: { ...authHeader() }, body: fd });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "");
      setHasPoster(true); setPosterBust(Date.now());
      toast.success("Poster updated");
    } catch (err) { toast.error(err.message || "Poster upload failed"); }
  };

  const downloadPNG = () => {
    const canvas = document.querySelector('[data-testid="cc-qr-canvas"] canvas');
    if (!canvas) return;
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/png"); a.download = "responsible-adventures-qr.png";
    a.click();
  };
  const downloadSVG = () => {
    const svg = svgWrapRef.current?.querySelector("svg");
    if (!svg) return;
    const data = new XMLSerializer().serializeToString(svg);
    const blob = new Blob(['<?xml version="1.0" encoding="UTF-8"?>\n' + data], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "responsible-adventures-qr.svg"; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1500);
  };

  if (loading) return <div className="flex items-center text-ink-secondary"><Loader2 className="w-5 h-5 animate-spin mr-2 text-terracotta" /> Loading…</div>;

  return (
    <div data-testid="calling-card-admin" className="max-w-4xl">
      <div className="flex items-center gap-2.5 mb-1">
        <div className="w-9 h-9 bg-moss rounded-sm flex items-center justify-center"><IdCard className="w-5 h-5 text-white" /></div>
        <div>
          <h2 className="font-display font-extrabold text-xl tracking-tight leading-none">Calling Card — Intro Trailer & QR</h2>
          <p className="font-mono text-[10px] uppercase tracking-widest text-ink-muted mt-1">Your 1-minute trailer behind a scannable QR code</p>
        </div>
      </div>
      <p className="text-sm text-ink-secondary mt-3 mb-6">
        Upload your self-hosted trailer, set your contact details, then download the QR code to print on cards & brochures.
        The QR opens <a href={introUrl} target="_blank" rel="noreferrer" className="text-terracotta underline">{introUrl}</a>.
      </p>

      <div className="grid lg:grid-cols-[1fr_300px] gap-6">
        {/* left: video + fields */}
        <div className="space-y-5">
          <div className="border border-sand-dark rounded-md bg-white p-5">
            <label className="font-mono text-[10px] uppercase tracking-widest text-ink-muted">Trailer video (MP4, max 120MB)</label>
            <div className="mt-3 aspect-video w-full rounded-sm overflow-hidden bg-ink/90 relative">
              {hasVideo ? (
                <video key={bust} src={`${API}/calling-card/video?t=${bust}`} controls className="w-full h-full object-contain" data-testid="cc-video-preview" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-white/50 text-sm">No trailer uploaded yet</div>
              )}
            </div>
            <label className="mt-3 inline-flex items-center gap-2 bg-ink hover:bg-ink/90 text-white px-4 h-10 rounded-sm text-sm font-semibold cursor-pointer transition-colors">
              <Upload className="w-4 h-4" /> {hasVideo ? "Replace trailer" : "Upload trailer"}
              <input type="file" accept="video/*" className="hidden" onChange={onUpload} data-testid="cc-video-input" disabled={uploading} />
            </label>
            {uploading && (
              <div className="mt-3">
                <div className="h-2 bg-sand rounded-full overflow-hidden"><div className="h-full bg-terracotta transition-all" style={{ width: `${progress}%` }} /></div>
                <p className="font-mono text-[11px] text-ink-muted mt-1">Uploading… {progress}%</p>
              </div>
            )}
            <div className="mt-4 pt-4 border-t border-sand-dark flex items-center gap-4">
              <div className="w-24 h-14 rounded-sm overflow-hidden bg-sand shrink-0 flex items-center justify-center">
                {hasPoster ? <img key={posterBust} src={`${API}/calling-card/poster?t=${posterBust}`} alt="poster" className="w-full h-full object-cover" /> : <span className="text-[10px] text-ink-muted">No poster</span>}
              </div>
              <div className="flex-1">
                <p className="font-mono text-[10px] uppercase tracking-widest text-ink-muted">Poster image (shown before the video loads)</p>
                <label className="mt-1.5 inline-flex items-center gap-2 bg-sand hover:bg-sand-dark text-ink px-3 h-9 rounded-sm text-sm font-semibold cursor-pointer transition-colors">
                  <Upload className="w-3.5 h-3.5" /> {hasPoster ? "Replace poster" : "Upload poster"}
                  <input type="file" accept="image/*" className="hidden" onChange={onPoster} data-testid="cc-poster-input" />
                </label>
              </div>
            </div>
          </div>

          <div className="border border-sand-dark rounded-md bg-white p-5 grid sm:grid-cols-2 gap-4">
            {FIELDS.map(([k, label, ph]) => (
              <div key={k} className={k === "tagline" ? "sm:col-span-2" : ""}>
                <label className="font-mono text-[10px] uppercase tracking-widest text-ink-muted">{label}</label>
                <input data-testid={`cc-field-${k}`} value={form[k] || ""} onChange={(e) => set(k, e.target.value)} placeholder={ph}
                  className="mt-1.5 w-full h-10 px-3 rounded-sm border border-sand-dark text-ink text-sm focus:border-moss outline-none" />
              </div>
            ))}
          </div>

          <button data-testid="cc-save" onClick={save} disabled={saving}
            className="flex items-center gap-2 bg-terracotta hover:bg-terracotta-hover text-white px-5 h-11 rounded-sm font-semibold transition-colors disabled:opacity-60">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save details
          </button>
        </div>

        {/* right: QR */}
        <div className="border border-sand-dark rounded-md bg-white p-5 h-fit">
          <div className="flex items-center gap-2 mb-4"><QrCode className="w-4 h-4 text-terracotta" /><h3 className="font-display font-bold text-sm">Your QR calling card</h3></div>
          <div className="bg-white border border-sand-dark rounded-sm p-4 flex items-center justify-center" data-testid="cc-qr-canvas">
            <QRCodeCanvas value={introUrl} size={200} level="M" includeMargin fgColor="#1a1a18" />
          </div>
          <div ref={svgWrapRef} className="hidden"><QRCodeSVG value={introUrl} size={400} level="M" includeMargin fgColor="#1a1a18" /></div>
          <div className="flex gap-2 mt-4">
            <button onClick={downloadPNG} data-testid="cc-qr-png" className="flex-1 flex items-center justify-center gap-1.5 bg-ink text-white h-10 rounded-sm text-sm font-semibold hover:bg-ink/90 transition-colors"><Download className="w-4 h-4" /> PNG</button>
            <button onClick={downloadSVG} data-testid="cc-qr-svg" className="flex-1 flex items-center justify-center gap-1.5 bg-sand text-ink h-10 rounded-sm text-sm font-semibold hover:bg-sand-dark transition-colors"><Download className="w-4 h-4" /> SVG</button>
          </div>
          <a href={introUrl} target="_blank" rel="noreferrer" className="mt-3 flex items-center justify-center gap-1.5 text-terracotta text-sm font-medium hover:underline">
            <ExternalLink className="w-3.5 h-3.5" /> Preview intro page
          </a>
        </div>
      </div>
    </div>
  );
}
